import React, { useEffect, useState } from 'react';
import { api } from './api';
import Icon from './Icon';
import './SystemAdmin.css';

// System-level panel: status of the automatic server-side reminder pass (last run +
// counts) plus a no-send "Preview" that explains, per reminder, why each would or
// wouldn't send right now — so a silent no-op (e.g. a reminder with no message) is
// never a mystery.
function fmt(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}

function ServerRunStatus() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewErr, setPreviewErr] = useState('');

  useEffect(() => {
    let alive = true;
    api.get('/api/cron/status')
      .then(d => { if (alive) setData(d); })
      .catch(e => { if (alive) setErr(e?.message || 'Could not load status'); });
    return () => { alive = false; };
  }, []);

  async function runPreview() {
    setLoadingPreview(true); setPreviewErr(''); setPreview(null);
    try { setPreview(await api.post('/api/cron/preview', {})); }
    catch (e) { setPreviewErr(e?.message || 'Preview failed'); }
    finally { setLoadingPreview(false); }
  }

  const last = data && data.last;
  const configured = data && data.configured;

  return (
    <div className="sa-panel">
      <div className="sa-panel-title"><Icon name="launch" size={16} /> Automatic processing (server)</div>
      <p className="sa-mls-hint">
        Reminders are sent automatically by the server on a daily schedule (≈ 8 AM Pacific) — no browser needs to
        be open. Use “Run on server now” below to trigger a pass on demand, or “Preview” to see why reminders
        would or wouldn’t send — without sending anything.
      </p>

      {err && <div className="sa-mls-hint" style={{ color: '#b91c1c' }}>⚠ {err}</div>}

      {!configured && !err && (
        <div className="sa-mls-hint" style={{ color: '#b45309' }}>
          ⚠ The scheduler isn’t configured yet — set <code>CRON_TOKEN</code> on the server and in the
          GitHub Actions secret to enable automatic sending.
        </div>
      )}

      {last ? (
        <div className="srs-grid">
          <div><span className="srs-k">Last run</span><span className="srs-v">{fmt(last.startedAt)}</span></div>
          <div><span className="srs-k">Status</span><span className="srs-v">{last.finishedAt ? (last.error ? `error: ${last.error}` : 'completed') : 'running…'}</span></div>
          <div><span className="srs-k">Trigger</span><span className="srs-v">{last.trigger}</span></div>
          <div><span className="srs-k">Agents</span><span className="srs-v">{last.agentsProcessed}</span></div>
          <div><span className="srs-k">Sent</span><span className="srs-v">{last.sent}</span></div>
          <div><span className="srs-k">Failed</span><span className="srs-v">{last.failed}</span></div>
          <div><span className="srs-k">Already sent</span><span className="srs-v">{last.skipped}</span></div>
        </div>
      ) : (!err && configured && (
        <div className="sa-mls-hint">No runs recorded yet.</div>
      ))}

      <div style={{ marginTop: 14 }}>
        <button className="rt-new-btn" onClick={runPreview} disabled={loadingPreview}>
          <Icon name="view" size={16} /> {loadingPreview ? 'Checking…' : 'Preview — what would send now?'}
        </button>
      </div>

      {previewErr && <div className="sa-mls-hint" style={{ color: '#b91c1c' }}>⚠ {previewErr}</div>}

      {preview && (
        <div className="srs-preview">
          <div className="srs-preview-summary">
            <strong>{preview.totals.wouldSend}</strong> would send · {preview.totals.skipped} skipped
            {preview.totals.haltedAgents ? ` · ${preview.totals.haltedAgents} agent(s) paused` : ''}
          </div>
          {preview.agents.length === 0 ? (
            <div className="sa-mls-hint">No agents to process.</div>
          ) : preview.agents.map((a, i) => (
            <div key={i} className="srs-preview-agent">
              <div className="srs-preview-agent-name">
                {a.agent}{a.halted ? <span className="srs-halt"> — paused ({a.halted})</span> : ''}
              </div>
              {!a.halted && a.items.length === 0 && <div className="srs-preview-empty">No reminders assigned.</div>}
              {a.items.map((it, j) => (
                <div key={j} className={`srs-preview-item ${it.status === 'send' ? 'send' : 'skip'}`}>
                  <span className="srs-pi-status">{it.status === 'send' ? '✓ would send' : '— skip'}</span>
                  <span className="srs-pi-who">{it.contactName} &lt;{it.email || 'no email'}&gt;</span>
                  <span className="srs-pi-rem">{it.reminderLabel}</span>
                  {it.status === 'skip' && <span className="srs-pi-reason">{it.reason}</span>}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ServerRunStatus;
